// The song/layer/settings model and its pure reducers (docs/08 §8.1–8.2).
// Reducers are immutable and side-effect free so they're unit-testable; the
// caller is responsible for disposing Tone nodes when layers are removed.

import type { Id, NoteEvent, QuantizeGrid, ScaleName } from "../util/types.ts";

export const SCHEMA_VERSION = 1;

export interface Settings {
  bpm: number;
  bars: number;
  key: string;
  scale: ScaleName;
  quantizeGrid: QuantizeGrid;
  quantizeStrength: number; // 0..1
  metronome: boolean;
  metronomeVolume: number; // dB
  countInBars: 0 | 1 | 2;
  latencyOffsetMs: number; // mic recording alignment
  reducedMotion: boolean;
  loopMode: "tempo-first" | "loop-first";
}

export interface BaseLayer {
  id: Id;
  name: string;
  kind: "event" | "audio";
  instrumentId: string;
  color: string;
  muted: boolean;
  solo: boolean;
  volumeDb: number;
  pan: number; // -1..1
  bars: number; // 1 = base loop; 2/4 = multiples (later)
}

export interface EventLayer extends BaseLayer {
  kind: "event";
  events: NoteEvent[]; // times relative to loop start (source of truth)
}

export interface AudioLayer extends BaseLayer {
  kind: "audio";
  audioRef: string;
  durationSec: number;
}

export type Layer = EventLayer | AudioLayer;

export interface Song {
  schemaVersion: number;
  id: Id;
  title: string;
  createdAt: number;
  updatedAt: number;
  settings: Settings;
  layers: Layer[];
}

export const DEFAULT_SETTINGS: Settings = {
  bpm: 90,
  bars: 4,
  key: "C",
  scale: "majorPentatonic",
  quantizeGrid: "16n",
  quantizeStrength: 0.9,
  metronome: true,
  metronomeVolume: -8,
  countInBars: 1,
  latencyOffsetMs: 120,
  reducedMotion: false,
  loopMode: "tempo-first",
};

export function createDefaultSong(id: Id, now: number): Song {
  return {
    schemaVersion: SCHEMA_VERSION,
    id,
    title: "Untitled",
    createdAt: now,
    updatedAt: now,
    settings: { ...DEFAULT_SETTINGS },
    layers: [],
  };
}

export function hasAudioLayer(song: Song): boolean {
  return song.layers.some((l) => l.kind === "audio");
}

// --- Reducers (pure) ------------------------------------------------------

export function addEventLayer(song: Song, layer: EventLayer): Song {
  return { ...song, layers: [...song.layers, layer] };
}

export function addAudioLayer(song: Song, layer: AudioLayer): Song {
  return { ...song, layers: [...song.layers, layer] };
}

export function deleteLayer(song: Song, id: Id): Song {
  return { ...song, layers: song.layers.filter((l) => l.id !== id) };
}

export function clearAll(song: Song): Song {
  return { ...song, layers: [] };
}

/**
 * Update settings. Guard: tempo is locked once an AudioLayer exists, because
 * recorded audio can't be time-stretched (docs/04 §4.2, §4.12). A blocked bpm
 * change is dropped; all other fields in the patch still apply.
 */
export function setSettings(song: Song, patch: Partial<Settings>): Song {
  const next: Partial<Settings> = { ...patch };
  if (next.bpm !== undefined && hasAudioLayer(song)) {
    delete next.bpm;
  }
  return { ...song, settings: { ...song.settings, ...next } };
}
